import type * as types from '../types';

import * as constants from '../constants';

import { mergeContext, pruneContext } from './context';
import { isRecordType } from './typeguards';

type Primitive = bigint | boolean | number | string;

const isPrimitive = (arg: unknown): arg is Primitive =>
  typeof arg === 'boolean' ||
  typeof arg === 'number' ||
  typeof arg === 'string' ||
  typeof arg === 'bigint';

enum ExpectedErrorArgType {
  Error = 'Error',
  Primitive = 'Primitive',
  Record = 'Record',
  Unknown = 'Unknown',
}

type ExpectedErrorArg<E extends ExpectedErrorArgType> = {
  [ExpectedErrorArgType.Error]: Error;
  [ExpectedErrorArgType.Primitive]: Primitive;
  [ExpectedErrorArgType.Record]: Record<string, unknown>;
  [ExpectedErrorArgType.Unknown]: unknown;
}[E];

type ArgProcessor<E extends ExpectedErrorArgType> = {
  readonly processor: (
    value: ExpectedErrorArg<E>,
    base: Omit<types.ParsedArgs, 'error'>,
  ) => types.ParsedArgs;
  readonly typeguard: (value: unknown) => value is ExpectedErrorArg<E>;
};

const ArgProcessors: { [key in ExpectedErrorArgType]: ArgProcessor<key> } = {
  [ExpectedErrorArgType.Error]: {
    processor: (error, { context, message }) => ({
      /* The error's message/name/stack are folded into the context by convertErrorToContext (via
         mergeContext), so the message is not added separately here — doing so would duplicate it
         under a collision-suffixed key. */
      context: mergeContext(context, error),
      error,
      message: message ? message : error.message,
    }),
    typeguard: value => value instanceof Error,
  },
  [ExpectedErrorArgType.Primitive]: {
    processor: (primitive, { context, message }) => {
      if (message) {
        return { context: mergeContext(context, { message: primitive }), message };
      }
      return { context, message: `${primitive}` };
    },
    typeguard: isPrimitive,
  },
  [ExpectedErrorArgType.Record]: {
    processor: (record, { context, message }) => {
      /* If the message is not explicitly defined, but the provided object has a 'message' key and
         the value is of type 'string' or 'number', use that as the message. */
      const fallbackMessage =
        typeof record.message === 'string' || typeof record.message === 'number'
          ? `${record.message}`
          : undefined;
      return { context: mergeContext(context, record), message: message ?? fallbackMessage };
    },
    typeguard: isRecordType,
  },
  [ExpectedErrorArgType.Unknown]: {
    processor: (value, { context, message }) => ({
      context: mergeContext(context, { [constants.UnknownContextKey]: value }),
      message,
    }),
    typeguard: (value): value is unknown =>
      !isPrimitive(value) &&
      !isRecordType(value) &&
      !(value instanceof Error) &&
      typeof value !== 'function',
  },
};

const parseBase = (
  arg1?: string | types.LogContext,
  arg2?: types.LogContext,
): types.ParsedArgs => ({
  context: pruneContext(typeof arg1 === 'string' ? arg2 : arg1),
  message: typeof arg1 === 'string' ? arg1 : undefined,
});

/**
 * Parses the possible argument combinations provided to one of the overloaded methods on a
 * {@link Logger} instance into a single object with a consistent format, {@link types.ParsedArgs}.
 *
 * The purpose of this method is to ensure that the information provided to the methods on the
 * {@link Logger} instance are processed in a way that allows the underlying strategies to
 * properly emit the logs with all of the information encapsulated in some fashion, without the loss
 * of any data.
 */
export const parseArgs = (
  arg0: unknown,
  arg1?: string | types.LogContext,
  arg2?: types.LogContext,
): types.ParsedArgs => {
  const base = parseBase(arg1, arg2);
  if (arg0 || typeof arg0 === 'boolean') {
    for (const key of Object.keys(ArgProcessors) as ExpectedErrorArgType[]) {
      const processor = ArgProcessors[key] as ArgProcessor<typeof key>;
      if (processor.typeguard(arg0)) {
        return processor.processor(arg0, base);
      }
    }
    return parseArgs(arg1, arg2);
  }
  if (typeof arg1 === 'string') {
    return base;
  }
  return { context: pruneContext(arg1) };
};
